/**
 * KkdKalemKatalogu.jsx — KKD kalem kataloğu (Yelek, Baret, Ayakkabı, ...): ekle, ad / simge / sıra düzenle, pasifleştir (backlog #374, backend #373).
 * Global Kurallar › KKD'de, seviye şablonunun (KkdKurallariKarti, fuarId 0) altında durur.
 *
 * Katalog fuardan bağımsızdır: silme yok, kalem pasifleştirilir — pasif kalem HİÇBİR fuarda istenmez ve kimse ona göre değerlendirilmez
 * (satırlar silinmez, geri açılabilir). Bu yüzden pasifleştirme, kalemi isteyen fuar sayısıyla birlikte onay ister. `kod` addan üretilir ve
 * sonradan değişmez. Simge, KkdKarti'nin paketlediği lucide adlarından seçilir (başkası kartta genel Shirt'e düşerdi; Baret'te simge bilerek yok:
 * HardHat İSG simgesi). Ad yalnızca Türkçe — backend'de kalem adı için İngilizce metin alanı yok.
 *
 * @param {Function} onChanged  katalog değişince çağrılır (yanındaki seviye şablonu yeniden okunsun)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table, Card, CardBody, CardHeader, FormGroup } from "reactstrap";
import { Pencil, Plus, RefreshCw, Save, Shirt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { listKkdKalemleri, createKkdKalemi, updateKkdKalemi } from "../../services/kkdKuraliService";
import { KkdIkon, KKD_IKON_ADLARI } from "../Kisi/Detay/KkdKarti";

const BOS_FORM = { ad: "", ikon: "", sira: "" };

export default function KkdKalemKatalogu({ onChanged }) {
  const { t } = useTranslation("yonetim");
  const [kalemler, setKalemler] = useState(null);
  const [hata, setHata] = useState(null);
  const [mesaj, setMesaj] = useState(null);
  const [calisiyor, setCalisiyor] = useState(false);
  const [yenile, setYenile] = useState(0);
  const [modal, setModal] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState(null); // null = yeni kalem
  const [form, setForm] = useState(BOS_FORM);
  const [pasifHedef, setPasifHedef] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const liste = await listKkdKalemleri({ includeInactive: true });
        if (!ignore) setKalemler(liste);
      } catch (e) {
        if (!ignore) { setHata(e.message); setKalemler([]); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [yenile]);

  useEffect(() => {
    if (!mesaj) return undefined;
    const zamanlayici = setTimeout(() => setMesaj(null), 4500);
    return () => clearTimeout(zamanlayici);
  }, [mesaj]);

  const yazildi = useCallback((metin) => {
    setMesaj(metin);
    setYenile((n) => n + 1);
    if (onChanged) onChanged();
  }, [onChanged]);

  function yeniAc() {
    setDuzenlenen(null);
    setForm(BOS_FORM);
    setHata(null);
    setModal(true);
  }

  function duzenleAc(kalem) {
    setDuzenlenen(kalem);
    setForm({ ad: kalem.ad ?? "", ikon: kalem.ikon ?? "", sira: kalem.sira ?? "" });
    setHata(null);
    setModal(true);
  }

  async function kaydet() {
    if (!form.ad.trim()) { setHata(t("kkdKatalog.adZorunlu")); return; }
    setCalisiyor(true);
    setHata(null);
    try {
      if (duzenlenen) {
        await updateKkdKalemi(duzenlenen.kkdKalemiId, { ad: form.ad, ikon: form.ikon, sira: form.sira });
        yazildi(t("kkdKatalog.guncellendi"));
      } else {
        await createKkdKalemi({ ad: form.ad, ikon: form.ikon, sira: form.sira });
        yazildi(t("kkdKatalog.eklendi"));
      }
      setModal(false);
    } catch (e) {
      setHata(e.message);
    } finally {
      setCalisiyor(false);
    }
  }

  async function aktiflikDegistir(kalem, aktif) {
    setCalisiyor(true);
    setHata(null);
    try {
      await updateKkdKalemi(kalem.kkdKalemiId, { aktif });
      yazildi(aktif ? t("kkdKatalog.aktiflestirildi") : t("kkdKatalog.pasiflestirildi"));
      setPasifHedef(null);
    } catch (e) {
      setHata(e.message);
      setPasifHedef(null);
    } finally {
      setCalisiyor(false);
    }
  }

  return (
    <>
      {hata && !modal && <Alert color="danger" toggle={() => setHata(null)} data-testid="kkd-katalog-hata">{hata}</Alert>}
      {mesaj && <Alert color="success" data-testid="kkd-katalog-mesaj">{mesaj}</Alert>}

      <Card data-testid="kkd-katalog">
        <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
          <Shirt size={16} className="text-primary" />
          <h6 className="card-title mb-0 flex-grow-1">
            {t("kkdKatalog.baslik")}
            {kalemler && <Badge color="light" className="text-dark ms-2">{kalemler.length}</Badge>}
          </h6>
          <Button color="light" size="sm" className="border" onClick={() => setYenile((n) => n + 1)} title={t("common:yenile")}>
            <RefreshCw size={14} />
          </Button>
          <Button color="dark" size="sm" onClick={yeniAc} data-testid="kkd-katalog-yeni">
            <Plus size={14} className="me-1" />{t("kkdKatalog.yeniKalem")}
          </Button>
        </CardHeader>
        <CardBody className="p-0">
          {kalemler == null ? (
            <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>
          ) : kalemler.length === 0 ? (
            <div className="text-muted text-center py-4">{t("kkdKatalog.bos")}</div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3" style={{ width: 60 }}>{t("kkdKatalog.sira")}</th>
                    <th>{t("kkdKatalog.ad")}</th>
                    <th style={{ width: 130 }}>{t("kkdKatalog.kod")}</th>
                    <th style={{ width: 90 }} title={t("kkdKatalog.fuarIpucu")}>{t("kkdKatalog.fuar")}</th>
                    <th style={{ width: 90 }}>{t("kkdKatalog.durum")}</th>
                    <th style={{ width: 170 }} />
                  </tr>
                </thead>
                <tbody>
                  {kalemler.map((k) => (
                    <tr key={k.kkdKalemiId} className={k.aktif ? "" : "opacity-50"} data-testid="kkd-katalog-satir">
                      <td className="ps-3 text-muted">{k.sira ?? "—"}</td>
                      <td>
                        <span className="d-inline-flex align-items-center gap-2 fw-semibold">
                          <KkdIkon ad={k.ikon} size={18} className="flex-shrink-0" />
                          {k.ad}
                        </span>
                      </td>
                      <td><code>{k.kod}</code></td>
                      <td className="text-muted">{k.fuarSayisi ?? "—"}</td>
                      <td>
                        {k.aktif
                          ? <Badge color="success-subtle" className="text-success">{t("kkdKatalog.aktif")}</Badge>
                          : <Badge color="light" className="text-muted border">{t("kkdKatalog.pasif")}</Badge>}
                      </td>
                      <td className="text-end pe-3 text-nowrap">
                        <Button color="link" size="sm" className="text-body p-1" disabled={calisiyor} title={t("common:duzenle")}
                                aria-label={t("common:duzenle")} onClick={() => duzenleAc(k)} data-testid="kkd-katalog-duzenle">
                          <Pencil size={14} />
                        </Button>
                        {k.aktif ? (
                          <Button color="link" size="sm" className="text-danger p-1" disabled={calisiyor}
                                  onClick={() => setPasifHedef(k)} data-testid="kkd-katalog-pasiflestir">
                            {t("kkdKatalog.pasiflestir")}
                          </Button>
                        ) : (
                          <Button color="link" size="sm" className="text-success p-1" disabled={calisiyor}
                                  onClick={() => aktiflikDegistir(k, true)} data-testid="kkd-katalog-aktiflestir">
                            {t("kkdKatalog.aktiflestir")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
        <div className="card-footer text-muted small">{t("kkdKatalog.aciklama")}</div>
      </Card>

      {/* ─── Kalem ekle / düzenle ─── */}
      <Modal isOpen={modal} toggle={() => !calisiyor && setModal(false)} centered>
        <ModalHeader toggle={() => !calisiyor && setModal(false)}>
          {duzenlenen ? t("kkdKatalog.duzenleBaslik") : t("kkdKatalog.yeniBaslik")}
        </ModalHeader>
        <ModalBody>
          {hata && <Alert color="danger" className="small py-2" data-testid="kkd-katalog-form-hata">{hata}</Alert>}
          <FormGroup>
            <Label size="sm" for="kkd-ad">{t("kkdKatalog.ad")}</Label>
            <Input id="kkd-ad" bsSize="sm" maxLength={100} value={form.ad} data-testid="kkd-form-ad"
                   onChange={(e) => setForm((f) => ({ ...f, ad: e.target.value }))} />
          </FormGroup>
          <FormGroup>
            <Label size="sm" for="kkd-ikon">{t("kkdKatalog.simge")}</Label>
            <div className="d-flex align-items-center gap-2">
              <KkdIkon ad={form.ikon} size={20} className="flex-shrink-0" />
              <Input id="kkd-ikon" type="select" bsSize="sm" value={form.ikon} data-testid="kkd-form-ikon"
                     onChange={(e) => setForm((f) => ({ ...f, ikon: e.target.value }))}>
                <option value="">{t("kkdKatalog.simgeYok")}</option>
                {KKD_IKON_ADLARI.map((ad) => <option key={ad} value={ad}>{ad}</option>)}
              </Input>
            </div>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label size="sm" for="kkd-sira">{t("kkdKatalog.sira")}</Label>
            <Input id="kkd-sira" type="number" bsSize="sm" value={form.sira} data-testid="kkd-form-sira"
                   placeholder={duzenlenen ? "" : t("kkdKatalog.siraSona")}
                   onChange={(e) => setForm((f) => ({ ...f, sira: e.target.value }))} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={calisiyor} onClick={() => setModal(false)}>{t("kkdKatalog.vazgec")}</Button>
          <Button color="primary" disabled={calisiyor} onClick={kaydet} data-testid="kkd-form-kaydet">
            {calisiyor ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}{t("common:kaydet")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ─── Pasifleştirme onayı: her fuarı aynı anda etkiler ─── */}
      <Modal isOpen={Boolean(pasifHedef)} toggle={() => !calisiyor && setPasifHedef(null)} centered>
        <ModalHeader toggle={() => !calisiyor && setPasifHedef(null)}>{t("kkdKatalog.pasifBaslik")}</ModalHeader>
        <ModalBody data-testid="kkd-pasif-metin">
          {t("kkdKatalog.pasifMetin", { ad: pasifHedef?.ad, n: pasifHedef?.fuarSayisi ?? 0 })}
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={calisiyor} onClick={() => setPasifHedef(null)}>{t("kkdKatalog.vazgec")}</Button>
          <Button color="danger" disabled={calisiyor} onClick={() => aktiflikDegistir(pasifHedef, false)} data-testid="kkd-pasif-onay">
            {calisiyor && <Spinner size="sm" className="me-1" />}{t("kkdKatalog.pasiflestir")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
